import { Camera } from "./camera.js";
import { LIGHTING_CONFIG, validLocalLight, type LocalLight } from "./lighting.js";
import type { AtmosphereQuality } from "./atmosphere.js";
import { mat4LookAt, mat4Multiply, mat4Perspective, type Vec3 } from "./math.js";

export const LOCAL_SHADOW_NEAR = 0.08;
// Six perspective faces laid out +X/-X/+Y then -Y/+Z/-Z, exactly matching GLSL.
const DIRECTIONS: readonly Vec3[] = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]];
const UPS: readonly Vec3[] = [[0, -1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1], [0, -1, 0], [0, -1, 0]];

export function localShadowFace(delta: readonly number[]): number {
  const [x, y, z] = delta as readonly [number, number, number];
  if (Math.abs(x) >= Math.abs(y) && Math.abs(x) >= Math.abs(z)) return x >= 0 ? 0 : 1;
  if (Math.abs(y) >= Math.abs(z)) return y >= 0 ? 2 : 3;
  return z >= 0 ? 4 : 5;
}

export class LocalShadowCamera extends Camera {
  private radius = 1;
  updateLight(light: LocalLight, face: number): void {
    this.radius = light.radius;
    this.position.splice(0, 3, ...light.position);
    const direction = DIRECTIONS[face]!;
    this.target.splice(0, 3, light.position[0] + direction[0], light.position[1] + direction[1], light.position[2] + direction[2]);
    this.view = mat4LookAt(this.position, this.target, UPS[face]!);
    this.projection = mat4Perspective(Math.PI / 2, 1, LOCAL_SHADOW_NEAR, light.radius);
    this.viewProjection = mat4Multiply(this.projection, this.view);
  }
  override isSphereVisible(x: number, y: number, z: number, radius: number): boolean {
    if (Math.hypot(x - this.position[0], y - this.position[1], z - this.position[2]) > this.radius + radius) return false;
    const m = this.viewProjection;
    for (let axis = 0; axis < 3; axis += 1) for (const sign of [-1, 1]) {
      const a = m[3]! + sign * m[axis]!;
      const b = m[7]! + sign * m[4 + axis]!;
      const c = m[11]! + sign * m[8 + axis]!;
      const d = m[15]! + sign * m[12 + axis]!;
      if (a * x + b * y + c * z + d < -radius * Math.hypot(a, b, c)) return false;
    }
    return true;
  }
}

/** Deterministic importance ranking with a 25% incumbent bonus. Only nearby,
 * currently illuminated lights qualify. Slot identity survives rank changes. */
export class LocalShadowSelection {
  readonly selected: LocalLight[] = [];
  update(lights: readonly LocalLight[], focus: readonly number[], quality: AtmosphereQuality): void {
    const previous = new Set(this.selected.map((light) => light.id));
    const ranked = lights.filter((light) => validLocalLight(light)
      && Math.hypot(light.position[0] - focus[0]!, light.position[1] - focus[1]!, light.position[2] - focus[2]!) < light.radius + 8)
      .map((light) => ({ light, score: light.intensity * (previous.has(light.id) ? 1.25 : 1)
        / (4 + (light.position[0] - focus[0]!) ** 2 + (light.position[1] - focus[1]!) ** 2 + (light.position[2] - focus[2]!) ** 2) }))
      .sort((a, b) => b.score - a.score || a.light.id.localeCompare(b.light.id))
      .slice(0, LIGHTING_CONFIG.quality[quality].localShadowLights);
    const wanted = new Map(ranked.map(({ light }) => [light.id, light]));
    const stable = this.selected.filter((light) => wanted.has(light.id)).map((light) => wanted.get(light.id)!);
    for (const { light } of ranked) if (!stable.some((entry) => entry.id === light.id)) stable.push(light);
    this.selected.splice(0, this.selected.length, ...stable);
  }
}

export interface LocalShadowResource {
  readonly id: string;
  readonly size: number;
  readonly textures: readonly [WebGLTexture, WebGLTexture];
  readonly framebuffers: readonly [WebGLFramebuffer, WebGLFramebuffer];
  signature: string;
  revision: number;
  strength: number;
}

export function localShadowSignature(light: LocalLight): string {
  return `${light.position.join(",")}:${light.radius}`;
}

export function destroyLocalShadow(gl: WebGL2RenderingContext, resource: LocalShadowResource): void {
  for (const texture of resource.textures) gl.deleteTexture(texture);
  for (const framebuffer of resource.framebuffers) gl.deleteFramebuffer(framebuffer);
}

/** Two six-face depth atlases per light: static cached geometry and current
 * actors. Visibility samples both; no static redraw or per-frame depth copies. */
export function createLocalShadow(gl: WebGL2RenderingContext, light: LocalLight, size: number): LocalShadowResource | undefined {
  const textures: WebGLTexture[] = [];
  const framebuffers: WebGLFramebuffer[] = [];
  try {
    for (let layer = 0; layer < 2; layer += 1) {
      const texture = gl.createTexture();
      const framebuffer = gl.createFramebuffer();
      if (texture) textures.push(texture);
      if (framebuffer) framebuffers.push(framebuffer);
      if (!texture || !framebuffer) throw new Error("local shadow allocation failed");
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.DEPTH_COMPONENT24, size * 3, size * 2, 0, gl.DEPTH_COMPONENT, gl.UNSIGNED_INT, null);
      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, texture, 0);
      gl.drawBuffers([gl.NONE]); gl.readBuffer(gl.NONE);
      if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) throw new Error("incomplete local shadow framebuffer");
    }
    return { id: light.id, size, textures: textures as [WebGLTexture, WebGLTexture], framebuffers: framebuffers as [WebGLFramebuffer, WebGLFramebuffer], signature: localShadowSignature(light), revision: -1, strength: 0 };
  } catch {
    for (const texture of textures) gl.deleteTexture(texture);
    for (const framebuffer of framebuffers) gl.deleteFramebuffer(framebuffer);
    return undefined;
  } finally {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
}
